import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import NoContent from '@components/NoContentComponent/NoContent';

import BookingOverview from './BookingOverview';
import BookingParticipantCard from './BookingParticipantCard';
import BookingParticipantDialog from './BookingParticipantDialog';

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  '& .MuiAccordionSummary-content.Mui-expanded': {
    marginBottom: 0
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  '& .MuiAccordionDetails-root': {
    paddingTop: 0
  }
}));

const BookingParticipants = ({
  fetchBookingParticipants,
  bookingParticipantsData = {},
  fetchBilling,
  bookingData = {},
  hasFetched,
  setHasFetched
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [overviewParticipants, setOverviewParticipants] = useState([]);

  useEffect(() => {
    if (!hasFetched) {
      fetchBookingParticipants();
      setHasFetched(true);
    }
  }, [fetchBookingParticipants, hasFetched, setHasFetched]);

  useEffect(() => {
    const booked = bookingData?.bookedParticipants || [];
    const waitingList = bookingData?.participantsOnWaitingList || [];
    setOverviewParticipants([...booked, ...waitingList]);
  }, [bookingData]);

  const handleViewInfo = (participant) => {
    setSelectedParticipant(participant);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedParticipant(null);
  };

  return (
    <Container sx={{ minHeight: '38vh' }}>
      <Accordion defaultExpanded sx={{ mt: 3 }}>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1-content'
          id='panel1-header'
        >
          <Typography component='span' sx={{ fontSize: '1rem', fontWeight: '600' }}>
            {Localize.get('EventDetails.bookingOverview')}
          </Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <BookingOverview
            data={overviewParticipants}
            bookingData={bookingData}
            fetchBilling={fetchBilling}
          />
        </StyledAccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel2-content'
          id='panel2-header'
        >
          <Typography component='span' sx={{ fontSize: '1rem', fontWeight: '600' }}>
            {Localize.get('EventDetails.participants')}
          </Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          {bookingParticipantsData && bookingParticipantsData.length ? (
            <>
              {bookingParticipantsData
                .sort((a, b) => {
                  // Sort by isBookingPerson (true goes first)
                  if (a.attendeeDto.isBookingPerson !== b.attendeeDto.isBookingPerson) {
                    return b.attendeeDto.isBookingPerson ? 1 : -1;
                  }

                  // Normalize null/undefined to ''
                  const firstNameA = a.firstName || '';
                  const firstNameB = b.firstName || '';
                  const lastNameA = a.lastName || '';
                  const lastNameB = b.lastName || '';

                  // Then sort by firstName
                  const firstNameCompare = firstNameA.localeCompare(firstNameB);
                  if (firstNameCompare !== 0) return firstNameCompare;

                  // Then sort by lastName
                  return lastNameA.localeCompare(lastNameB);
                })
                .map((participant, index) => (
                  <BookingParticipantCard
                    key={index}
                    participant={participant}
                    onViewInfo={() => handleViewInfo(participant)}
                  />
                ))}
            </>
          ) : (
            <Box data-testid={'bookingParticipantsListNoContent'}>
              <NoContent
                infoType={Localize.get('EventDetails.noBookingParticipants')}
                infoMessage={Localize.get('EventDetails.selectNewBooking')}
              />
            </Box>
          )}
        </StyledAccordionDetails>
      </Accordion>

      {selectedParticipant && (
        <BookingParticipantDialog
          open={openDialog}
          onClose={handleCloseDialog}
          participantData={selectedParticipant}
        />
      )}
    </Container>
  );
};

export default BookingParticipants;
